import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/TechnicianDashboard.css';
import API_BASE_URL from '../config';
import { getToken } from '../auth';

const NAV_ITEMS = [
  { key: 'jobs',     label: 'Past Hires',        icon: '📋' },
  { key: 'billing',  label: 'Billing & Payment', icon: '💳' },
  { key: 'profile',  label: 'Profile',           icon: '👤' },
  { key: 'logout',   label: 'Sign Out',          icon: '🚪' },
];

const ALLOWED_CATEGORIES = [
  { value: 'plumber', label: 'Plumbers' },
  { value: 'electrician', label: 'Electricians' },
  { value: 'hvac', label: 'HVAC Technician' },
  { value: 'painter', label: 'Painter' },
  { value: 'locksmith', label: 'Locksmith' },
];

function TechnicianDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const initialSection = searchParams.get('section') || 'jobs';
  const [activeSection, setActiveSection] = useState(initialSection === 'profile' ? 'profile' : 'jobs');
  const localUser = JSON.parse(localStorage.getItem('user')) || {};
  const [profile, setProfile] = useState(localUser);
  const token = getToken();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', phone: '', address: '', email: '' });

  const [editNotice, setEditNotice] = useState(null);
  const [myCategories, setMyCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [newCertFile, setNewCertFile] = useState(null);
  const [catNotice, setCatNotice] = useState(null);
  const [pastHires, setPastHires] = useState([]);
  const [hiresLoading, setHiresLoading] = useState(false);

  useEffect(() => {
    const fetchMyProfile = async () => {
      if (!token) {
        navigate('/technician/signin');
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/technician/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            navigate('/technician/signin');
          }
          return;
        }
        const data = await res.json();
        const merged = { ...localUser, ...data, role: 'technician' };
        setProfile(merged);
        localStorage.setItem('user', JSON.stringify(merged));
      } catch (err) {
        console.error('Failed to fetch technician profile:', err);
      }
    };

    fetchMyProfile();
  }, [token, navigate]);

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    setCatLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/technician/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMyCategories(data.categories || []);
      }
    } catch { /* ignore */ }
    finally { setCatLoading(false); }
  }, [token]);

  useEffect(() => {
    if (activeSection === 'profile') fetchCategories();
  }, [activeSection, fetchCategories]);

  const fetchPastHires = useCallback(async () => {
    if (!token) return;
    setHiresLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/technician/past-hires`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPastHires(data.hires || []);
      }
    } catch { /* ignore */ }
    finally { setHiresLoading(false); }
  }, [token]);

  useEffect(() => {
    if (activeSection === 'jobs') fetchPastHires();
  }, [activeSection, fetchPastHires]);

  const startEditing = () => {
    setEditForm({
      full_name: profile.full_name || '',
      phone: profile.phone || '',
      address: profile.address || profile.street || '',
      email: profile.email || '',
    });
    setEditing(true);
    setEditNotice(null);
  };

  const saveProfile = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/technician/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        const data = await res.json();
        const updated = {
          ...profile,
          full_name: data.full_name,
          phone: data.phone,
          address: data.address,
          email: data.email,
        };
        setProfile(updated);
        localStorage.setItem('user', JSON.stringify(updated));
        setEditing(false);
        setEditNotice({ type: 'success', text: 'Profile updated!' });
        setTimeout(() => setEditNotice(null), 2500);
      } else {
        const err = await res.json().catch(() => ({}));
        setEditNotice({ type: 'error', text: err.detail || 'Update failed.' });
      }
    } catch {
      setEditNotice({ type: 'error', text: 'Network error.' });
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.trim() || !newCertFile) {
      setCatNotice({ type: 'error', text: 'Category and certificate are required.' });
      return;
    }
    const formData = new FormData();
    formData.append('category', newCat.trim());
    formData.append('certificate', newCertFile);
    try {
      const res = await fetch(`${API_BASE_URL}/technician/categories`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        setCatNotice({ type: 'success', text: 'Category added. Pending approval.' });
        setNewCat(''); setNewCertFile(null);
        fetchCategories();
      } else {
        const err = await res.json().catch(() => ({}));
        setCatNotice({ type: 'error', text: err.detail || 'Failed to add category.' });
      }
    } catch {
      setCatNotice({ type: 'error', text: 'Network error.' });
    }
    setTimeout(() => setCatNotice(null), 3000);
  };

  const initials = (profile.full_name || 'T').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const displayType = profile.tech_type === 'other' ? (profile.custom_tech_type || 'Technician') : (profile.tech_type || 'Technician');
  const profileRows = useMemo(
    () => [
      ['Full Name', profile.full_name || '—'],
      ['Email', profile.email || '—'],
      ['Phone', profile.phone || '—'],
      ['Trade', displayType],
      ['Address', profile.address || profile.street || '—'],
      ['City', profile.city || '—'],
      ['Province', profile.province || '—'],
      ['Status', 'Approved'],
    ],
    [displayType, profile.address, profile.city, profile.email, profile.full_name, profile.phone, profile.province, profile.street],
  );

  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleNav = (key) => {
    setSidebarOpen(false);
    if (key === 'billing') { navigate('/billing'); return; }
    if (key === 'logout')  { handleSignOut(); return; }
    setActiveSection(key);
    setSearchParams({ section: key });
  };

  return (
    <div className="td-shell">

      {/* ── Top header ── */}
      <header className="td-header">
        <button className="td-hamburger" onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
        <span className="td-header__logo">SkillArea Technician</span>
        <div className="td-header__user">
          <span className="td-header__name">{profile.full_name || 'Technician'}</span>
          <div className="td-avatar">{initials}</div>
        </div>
      </header>

      <div className="td-tabs">
        <button className="td-tabs__item td-tabs__item--active" onClick={() => navigate('/technician/dashboard')}>
          Home
        </button>
        <button className="td-tabs__item" onClick={() => navigate('/technician/messages')}>
          Messages
        </button>
        <button className="td-tabs__item" onClick={() => navigate('/technician/support')}>
          Support
        </button>
      </div>

      <div className="td-body">

        {/* ── Sidebar ── */}
        <aside className={`td-sidebar${sidebarOpen ? ' td-sidebar--open' : ''}`}>
          <div className="td-sidebar__profile">
            <div className="td-sidebar__avatar">{initials}</div>
            <p className="td-sidebar__name">{profile.full_name || 'Technician'}</p>
            <span className="td-badge td-badge--approved">Approved</span>
            <p className="td-sidebar__trade">{displayType}</p>
          </div>

          <nav className="td-nav">
            {NAV_ITEMS.map(item => (
              <button
                key={item.key}
                className={`td-nav__item${activeSection === item.key ? ' td-nav__item--active' : ''}${item.key === 'logout' ? ' td-nav__item--logout' : ''}`}
                onClick={() => handleNav(item.key)}
              >
                <span className="td-nav__icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && <div className="td-overlay" onClick={() => setSidebarOpen(false)} />}

        {/* ── Main content ── */}
        <main className="td-main">
          <section className="td-hero">
            <div>
              <p className="td-hero__eyebrow">Workspace</p>
              <h1 className="td-hero__title">Welcome back, {profile.full_name || 'Technician'}</h1>
              <p className="td-hero__sub">Use Inbox as your daily home, then jump to profile, support, and past hirers from the menu.</p>
            </div>
            <button className="td-hero__inbox" onClick={() => navigate('/technician/messages')}>Open Inbox</button>
          </section>

          {activeSection === 'jobs' && (
            <section>
              <div className="td-section-header">
                <h2 className="td-section-title">Past Hires</h2>
              </div>

              {hiresLoading ? (
                <div className="td-empty"><p>Loading hires…</p></div>
              ) : pastHires.length === 0 ? (
                <div className="td-empty">
                  <div className="td-empty__icon">📋</div>
                  <p className="td-empty__title">No hires yet</p>
                  <p className="td-empty__sub">Your hire history will appear here once hirers engage your services.</p>
                </div>
              ) : (
                <div className="td-profile-card">
                  {pastHires.map((h) => (
                    <div className="td-profile-row" key={h.id}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.92rem' }}>{h.hirer_name}</p>
                        <p style={{ margin: '0.15rem 0 0', fontSize: '0.78rem', color: '#888' }}>
                          Hired {h.created_at ? new Date(h.created_at).toLocaleDateString() : '—'}
                          {h.completed_at ? ` — Completed ${new Date(h.completed_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <span className={`td-badge td-badge--${h.status === 'completed' ? 'approved' : 'pending'}`}>
                        {h.status === 'completed' ? 'Completed' : h.status === 'pending' ? 'Awaiting Approval' : 'Active'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {activeSection === 'profile' && (
            <section>
              <div className="td-section-header">
                <h2 className="td-section-title">My Profile</h2>
                {!editing && (
                  <button className="td-edit-btn" onClick={startEditing}>Edit Profile</button>
                )}
              </div>

              {editNotice && (
                <div className={`td-notice td-notice--${editNotice.type}`}>{editNotice.text}</div>
              )}

              {editing ? (
                <div className="td-profile-card">
                  <div className="td-profile-row">
                    <span className="td-profile-label">Full Name</span>
                    <input
                      className="td-profile-input"
                      value={editForm.full_name}
                      onChange={(e) => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                    />
                  </div>
                  <div className="td-profile-row">
                    <span className="td-profile-label">Phone</span>
                    <input
                      className="td-profile-input"
                      value={editForm.phone}
                      onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                    />
                  </div>
                  <div className="td-profile-row">
                    <span className="td-profile-label">Address</span>
                    <input
                      className="td-profile-input"
                      value={editForm.address}
                      onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))}
                    />
                  </div>
                  <div className="td-profile-row">
                    <span className="td-profile-label">Email</span>
                    <input
                      className="td-profile-input"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                  <div className="td-profile-row td-profile-row--actions">
                    <button className="td-save-btn" onClick={saveProfile}>Save</button>
                    <button className="td-cancel-btn" onClick={() => setEditing(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="td-profile-card">
                  {profileRows.map(([label, value]) => (
                    <div className="td-profile-row" key={label}>
                      <span className="td-profile-label">{label}</span>
                      <span className={`td-profile-value${label === 'Status' ? ' td-badge td-badge--approved' : ''}`}>{value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Categories ── */}
              <div className="td-section-header" style={{ marginTop: '1.5rem' }}>
                <h2 className="td-section-title">My Categories</h2>
              </div>

              {catNotice && (
                <div className={`td-notice td-notice--${catNotice.type}`}>{catNotice.text}</div>
              )}

              {catLoading ? (
                <div className="td-empty"><p>Loading categories...</p></div>
              ) : myCategories.length === 0 ? (
                <div className="td-empty">
                  <p className="td-empty__sub">No extra categories yet. Add one below.</p>
                </div>
              ) : (
                <div className="td-profile-card">
                  {myCategories.map((c) => (
                    <div className="td-profile-row" key={c.id}>
                      <span className="td-profile-label">{c.custom_category || c.category}</span>
                      <span className={`td-badge td-badge--${c.status}`}>{c.status}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '1rem', marginBottom: '0.65rem' }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '0.92rem', color: '#1f2937' }}>
                  Want to register under another technician category?
                </p>
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
                  Select your desired category, then upload supporting certification for review.
                </p>
              </div>

              <form className="td-cat-form" onSubmit={addCategory}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4b5563' }}>
                  Select category
                </label>
                <select
                  className="td-profile-input"
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {ALLOWED_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4b5563' }}>
                  Upload your certificate for the selected category
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setNewCertFile(e.target.files?.[0] || null)}
                  required
                />
                <button type="submit" className="td-save-btn">Add Category</button>
              </form>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default TechnicianDashboard;